import { formatINR } from "@templeos/shared";
import { useToast } from "@templeos/ui";
import { api, NetworkError } from "../lib/api.js";
import { db } from "../lib/db.js";
import { logPrint, printJobs, printProblem, saleJobs } from "../lib/print.js";
import type { ItemKind, PayMode, QueuedSale, Ticket } from "../lib/types.js";
import { useSession } from "../session.js";
import { SupervisorModal } from "./SupervisorModal.js";

/** GET /api/core/receipts/:id (foundation getReceipt). */
export interface ServerReceipt {
  id: string; number: string; receipt_date: string; created_at: string; total: number; status: "issued" | "cancelled"; public_token: string;
  client_ref: string | null; counter_code: string | null; cashier: string | null; reprint_count: number; payer_name: string | null;
  lines: { line_no: number; kind: string; item_id: string | null; description: string; qty: number; unit_price: number; amount: number; service_date: string | null; slot: string | null }[];
  payments: { mode: string; amount: number; reference: string | null }[];
}

export const REPRINT_REASONS = ["Printer jam / paper out", "Print unreadable", "Devotee lost the slip", "Wrong copy handed over"] as const;
export const VOID_REASONS = ["Wrong ticket issued", "Devotee cancelled before entry", "Duplicate bill by mistake", "Payment not received"] as const;

/** Ticket codes for a receipt, from the public pass endpoint (T1b); cancelled tickets are never reprinted. */
async function passTickets(publicToken: string): Promise<Ticket[]> {
  try {
    const p = await api.get<{ tickets: (Ticket & { status: string })[] }>(`/api/public/counters/pass/${publicToken}`);
    return p.tickets.filter((t) => t.status !== "void");
  } catch (e) {
    if (e instanceof NetworkError) throw e;
    return [];
  }
}

/** A printable sale from the server's copy of a receipt (history rows may come from another terminal). */
export async function receiptToSale(r: ServerReceipt, local?: QueuedSale): Promise<QueuedSale> {
  const tickets = local?.tickets?.length ? local.tickets : r.lines.some((l) => l.kind === "ticket") ? await passTickets(r.public_token) : [];
  const seq = Number(/-(\d{5})-/.exec(r.client_ref ?? "")?.[1] ?? 0);
  return {
    clientRef: r.client_ref ?? r.number, n: 0, seq, day: r.receipt_date, createdAt: new Date(r.created_at).toISOString(), counterCode: r.counter_code ?? "",
    cashier: r.cashier ?? "", shiftId: null, cart: [], prevHash: "", hash: "", status: "synced", attempts: 0,
    lines: r.lines.map((l) => ({ name: l.description, qty: l.qty, unitPrice: l.unit_price, amount: l.amount, kind: l.kind as ItemKind | "donation", itemId: l.item_id ?? undefined, slot: l.slot, serviceDate: l.service_date })),
    payments: r.payments.map((p) => ({ mode: p.mode as PayMode, amount: p.amount, reference: p.reference })),
    total: r.total, receiptId: r.id, number: r.number, publicToken: r.public_token, tickets,
  };
}

/** Duplicate print with a supervisor's approval; logged by the server with the reason (SOW Modules 2 and 4). */
export function ReprintModal({ target, onClose }: { target: { receiptId: string; local?: QueuedSale } | null; onClose: () => void }) {
  const s = useSession();
  const toast = useToast();
  return (
    <SupervisorModal open={Boolean(target)} title="Reprint as DUPLICATE" reasons={REPRINT_REASONS} confirmLabel="Approve and print duplicate" onClose={onClose}
      onApprove={async ({ username, password, reason }) => {
        if (!target) return;
        const r = await api.post<ServerReceipt>(`/api/core/receipts/${target.receiptId}/reprint`, { reason, supervisor: { username, password } });
        const sale = await receiptToSale(r, target.local);
        const printed = await printJobs(s.config?.printerUrl ?? null, saleJobs(sale, s.slip, { reason, approvedBy: username }));
        if (printed.printed) logPrint("duplicate", target.receiptId);
        const problem = printProblem(printed);
        toast(problem ? `Duplicate approved. ${problem}.` : `Duplicate of ${r.number} printed.`, problem ? "danger" : "success");
      }}>
      <p style={{ margin: 0 }}>The copy is marked <strong>DUPLICATE</strong>. The reprint, its reason and the approving supervisor are recorded in the audit trail.</p>
    </SupervisorModal>
  );
}

/** Void an issued bill: supervisor credentials, reason; tickets are cancelled with it (SOW Module 2). */
export function VoidModal({ target, onClose, onDone }: { target: { receiptId: string; number: string; total: number } | null; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  return (
    <SupervisorModal open={Boolean(target)} title={`Void bill ${target?.number ?? ""}`} reasons={VOID_REASONS} confirmLabel="Approve void" danger onClose={onClose}
      onApprove={async ({ username, password, reason }) => {
        if (!target) return;
        await api.post("/api/counters/void", { receiptId: target.receiptId, reason, supervisor: { username, password } });
        const local = await db.sales.filter((x) => x.receiptId === target.receiptId).first();
        if (local) await db.sales.update(local.clientRef, { voided: true });
        toast(`Bill ${target.number} voided. Return ${formatINR(target.total)} to the devotee.`, "success");
        onDone();
      }}>
      <p style={{ margin: 0 }}>Voiding cancels the receipt and every ticket on it, reverses the accounts entry and cannot be undone.</p>
    </SupervisorModal>
  );
}
